import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Loader2, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOCIAL_FIELDS = [
  { key: 'instagram', label: 'Instagram', placeholder: '@yourusername', color: '#E1306C' },
  { key: 'twitter', label: 'X (Twitter)', placeholder: '@yourusername', color: '#1DA1F2' },
  { key: 'tiktok', label: 'TikTok', placeholder: '@yourusername', color: '#FE2C55' },
  { key: 'discord', label: 'Discord', placeholder: 'username#1234', color: '#5865F2' },
];

export default function SocialSettings() {
  const navigate = useNavigate();
  const [socials, setSocials] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchSocials();
  }, []);

  const fetchSocials = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/user/socials`, { withCredentials: true });
      setSocials(data || {});
    } catch (err) {
      console.error('Fetch socials error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/user/socials`, socials, { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Save socials error:', err);
      alert('Failed to save social links');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pb-24">
      <div className="sticky top-0 z-40 bg-[#050505]/95 backdrop-blur-sm border-b border-white/5 px-4 h-12 flex items-center gap-3">
        <button onClick={() => navigate('/settings')} className="text-white" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="font-bold text-white">Social Links</h1>
      </div>

      <div className="p-4 space-y-4">
        <p className="text-xs text-[#71717A]">Add your social links so other creators can find you</p>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 text-[#E11D48] animate-spin" />
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {SOCIAL_FIELDS.map((field) => (
                <div key={field.key} className="p-3 bg-[#1A1D24] rounded-xl border border-white/5 space-y-2">
                  <Label className="text-xs font-medium" style={{ color: field.color }}>{field.label}</Label>
                  <Input
                    value={socials[field.key] || ''}
                    onChange={(e) => setSocials(prev => ({ ...prev, [field.key]: e.target.value }))}
                    placeholder={field.placeholder}
                    className="bg-[#0F1115] border-white/10 text-white rounded-lg text-sm"
                    data-testid={`social-${field.key}-input`}
                  />
                </div>
              ))}
            </div>

            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-lg"
              data-testid="save-socials-btn"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving...</>
              ) : saved ? (
                <><Check className="w-4 h-4 mr-2" />Saved!</>
              ) : (
                'Save Links'
              )}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
